/*
 * The painter view's own field strokes (docs/STYLE.md §12.16): for the polders tools/painterStrokes
 * measured on the painting, the layout-plane ditch angle and spacing at which the painter view
 * draws the lines the painter drew. Walk and fly views keep the surveyed ditches; painted.frag fades
 * these in with the painter amount. The painter view has no layout, so each painted polder brings
 * its polygon, and a fragment takes the first polygon, in layout order, that holds its layout point:
 * the order in which the site partition gives polders their ground. fields.glsl mirrors
 * painterStrokeRegionAt.
 */

import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { Vec2 } from '../geo/types';
import type { PainterStrokesFile } from '../data/generated/painterStrokes';
import { createAjvFactory } from '../data/validate';
import schema from '../data/schema/painterStrokes.schema.json';

export interface PainterStrokeRegion {
  readonly id: string;
  readonly ditchAngle: number;
  readonly ditchSpacing: number;
  readonly polygon: readonly Vec2[];
  /** minX, minY, maxX, maxY of the polygon. */
  readonly box: readonly [number, number, number, number];
}

let validate: ReturnType<ReturnType<typeof createAjvFactory>['compile']> | null = null;

/* What the schema cannot see: painted ditches come with the polygon they are looked up in, and only
   on as many agreeing tiles as the method asks for. */
function consistencyErrors(file: PainterStrokesFile): string[] {
  return Object.entries(file.polders).flatMap(([id, strokes]) => {
    if (!strokes) return [];
    if (strokes.painter && !strokes.polygon) return [`${id} has painted ditches without a polygon`];
    if (!strokes.painter && strokes.polygon) return [`${id} has a polygon without painted ditches`];
    if (strokes.painter && strokes.agreeing < file.method.minTiles) {
      return [
        `${id} has painted ditches on ${strokes.agreeing} agreeing tiles, fewer than ${file.method.minTiles}`,
      ];
    }
    return [];
  });
}

export function parsePainterStrokes(value: unknown): Result<PainterStrokesFile, readonly string[]> {
  validate ??= createAjvFactory().compile(schema);
  if (!validate(value)) {
    return err(
      (validate.errors ?? []).map((e) => `${e.instancePath || '/'}: ${e.message ?? 'invalid'}`)
    );
  }
  const file = value as PainterStrokesFile;
  const errors = consistencyErrors(file);
  return errors.length > 0 ? err(errors) : ok(file);
}

function boxOf(polygon: readonly Vec2[]): readonly [number, number, number, number] {
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

/** The painted polders, in file order, which is layout order. */
export function painterStrokeRegions(file: PainterStrokesFile): readonly PainterStrokeRegion[] {
  return Object.entries(file.polders).flatMap(([id, strokes]) => {
    if (!strokes?.painter || !strokes.polygon) return [];
    const polygon = strokes.polygon.map((p): Vec2 => [p[0], p[1]]);
    return [{ id, ...strokes.painter, polygon, box: boxOf(polygon) }];
  });
}

/* Even-odd crossings of a ray toward +x, over the edges from the last corner round to the first. */
function holds(polygon: readonly Vec2[], p: Vec2): boolean {
  let inside = false;
  let a = polygon[polygon.length - 1]!;
  for (const b of polygon) {
    if (
      b[1] > p[1] !== a[1] > p[1] &&
      p[0] < ((a[0] - b[0]) * (p[1] - b[1])) / (a[1] - b[1]) + b[0]
    ) {
      inside = !inside;
    }
    a = b;
  }
  return inside;
}

export function painterStrokeRegionAt(
  regions: readonly PainterStrokeRegion[],
  p: Vec2
): PainterStrokeRegion | null {
  for (const region of regions) {
    const [minX, minY, maxX, maxY] = region.box;
    if (p[0] < minX || p[1] < minY || p[0] > maxX || p[1] > maxY) continue;
    if (holds(region.polygon, p)) return region;
  }
  return null;
}

export interface PainterStrokeUniformData {
  readonly boxes: readonly (readonly [number, number, number, number])[];
  /** ditchAngle, ditchSpacing, first corner, corner count. */
  readonly values: readonly (readonly [number, number, number, number])[];
  /** The corners of every polygon in turn, two to a vector. */
  readonly vertexPairs: readonly (readonly [number, number, number, number])[];
}

export function painterStrokeUniformData(
  regions: readonly PainterStrokeRegion[]
): PainterStrokeUniformData {
  const firsts = regions.map((_, i) =>
    regions.slice(0, i).reduce((n, r) => n + r.polygon.length, 0)
  );
  const corners = regions.flatMap((r) => r.polygon);
  const vertexPairs = Array.from({ length: Math.ceil(corners.length / 2) }, (_, i) => {
    const a = corners[2 * i]!;
    const b = corners[2 * i + 1] ?? a;
    return [a[0], a[1], b[0], b[1]] as const;
  });
  return {
    boxes: regions.map((r) => r.box),
    values: regions.map(
      (r, i) => [r.ditchAngle, r.ditchSpacing, firsts[i]!, r.polygon.length] as const
    ),
    vertexPairs,
  };
}
